import { Heading, HFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { PageLoading } from 'components/loading'
import { getLotacaoResponsavel } from 'components/lotacao-responsavel/utils/utils-lotacaoResponsavel'
import { TourTarget } from 'components/tour/components/TourTarget'
import { css } from 'emotion'
import { useCidadaoAtualizacaoQuery } from 'graphql/hooks.generated'
import { useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'

import { CidadaoFciSincronizarCadsusButton } from './cadsus/components/CidadaoFciSincronizarCadsusButton'
import { ModalBuscaInformacoesCadsus } from './cadsus/components/ModalBuscaInformacoesCadsus'
import { ModalStatusSincronizacaoCadsus } from './cadsus/components/ModalStatusSincronizacaoCadsus'
import { useCadsusFciEditView } from './cadsus/hooks/useCadsusEditView'
import { CidadaoForm } from './CidadaoForm'
import { convertToCidadaoFormModel, mergeCadsusToFormModel } from './converter-cidadao'
import { CidadaoTourIdEnum } from './tour-cidadao'

interface UrlParams {
  cidadaoId: string
}

export function CidadaoEditView() {
  const match = useRouteMatch<UrlParams>()
  const cidadaoId = match.params.cidadaoId

  const {
    cidadaoCadsus,
    showSyncCadsusButton,
    setCidadaoCadsus,
    needLoginGovBr,
    isCadsusHabilitado,
    syncCadsus,
    resultBuscaInfoCidadaoState,
    isModalBuscaCidadaoOpenState,
    setIsModalBuscaCidadaoOpenState,
    isInitialCadsusSearchPending,
    isModalSincronizacaoLoading,
    cadsusSyncResult,
    isModalSincronizacaoOpen,
    setIsModalSincronizacaoOpen,
    hasCnsState,
  } = useCadsusFciEditView({ cidadaoId })
  const [isCadsusButtonModalOpen, setIsCadsusButtonModalOpen] = useState(false)

  const { data, loading } = useCidadaoAtualizacaoQuery({
    variables: {
      id: cidadaoId,
    },
    fetchPolicy: 'network-only',
  })

  const cidadaoFciAtualizado = data?.cidadao

  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const { hasAuthorization } = useSession()
  const isUsuarioAps =
    acesso?.unidadeSaude?.tipo?.isTipoAps === true &&
    hasAuthorization(Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar.cadastroIndividualAps)
  const isCadastroOrigemForaAps =
    !!cidadaoFciAtualizado?.cidadaoNucleoFamiliar?.unidadeSaude &&
    cidadaoFciAtualizado?.cidadaoNucleoFamiliar?.unidadeSaude?.tipo?.isTipoAps !== true

  const initialValues = useMemo(() => {
    if (!cidadaoFciAtualizado) return null

    const lotacaoLogada = getLotacaoResponsavel(profissional, acesso.cbo, acesso.equipe, acesso.unidadeSaude, acesso.id)

    const localValuesBase = convertToCidadaoFormModel(cidadaoFciAtualizado, lotacaoLogada)
    const localValues =
      isCadastroOrigemForaAps && isUsuarioAps
        ? {
            ...localValuesBase,
            responsabilidadeAcompanhamento: {
              ...localValuesBase.responsabilidadeAcompanhamento,
              responsavel: lotacaoLogada,
            },
          }
        : localValuesBase

    if (cidadaoCadsus) {
      return mergeCadsusToFormModel(localValues, cidadaoCadsus)
    }

    return localValues
  }, [
    cidadaoFciAtualizado,
    cidadaoCadsus,
    profissional,
    acesso.cbo,
    acesso.equipe,
    acesso.unidadeSaude,
    acesso.id,
    isCadastroOrigemForaAps,
    isUsuarioAps,
  ])

  const isLoadingData = loading || !initialValues
  const isAutoTourBlocked =
    isInitialCadsusSearchPending || isModalBuscaCidadaoOpenState || isModalSincronizacaoOpen || isCadsusButtonModalOpen

  return (
    <PageContent type='filled'>
      <Breadcrumb title='Visualização' to={`${CIDADAO_PATH}/${cidadaoId}`} />
      <Breadcrumb title='Edição' />
      <HFlow
        justifyContent='space-between'
        style={css`
          margin-bottom: 1rem;
        `}
      >
        <TourTarget id={CidadaoTourIdEnum.PAGE_TITLE} inline>
          <Heading level={1}>Cadastro individual</Heading>
        </TourTarget>

        {showSyncCadsusButton && (
          <TourTarget id={CidadaoTourIdEnum.CADSUS_BUTTON}>
            <CidadaoFciSincronizarCadsusButton
              cns={cidadaoCadsus.dadosPessoais.cns}
              setCidadaoCadsus={setCidadaoCadsus}
              cidadaoId={cidadaoId}
              needLoginGovbr={needLoginGovBr}
              isCadsusHabilitado={isCadsusHabilitado}
              onModalOpenChange={setIsCadsusButtonModalOpen}
            />
          </TourTarget>
        )}
      </HFlow>
      {isLoadingData ? (
        <PageLoading message='Carregando dados' />
      ) : (
        <CidadaoForm
          initialValues={initialValues}
          sincronizarCadsus={syncCadsus}
          needLoginGovBr={needLoginGovBr}
          isCadsusHabilitado={isCadsusHabilitado}
          isUsuarioAps={isUsuarioAps}
          isCadastroOrigemForaAps={isCadastroOrigemForaAps}
          isCadsusButtonVisible={showSyncCadsusButton}
          isAutoTourBlocked={isAutoTourBlocked}
        />
      )}
      <ModalBuscaInformacoesCadsus
        result={resultBuscaInfoCidadaoState}
        isModalOpen={isModalBuscaCidadaoOpenState}
        setModalState={setIsModalBuscaCidadaoOpenState}
        isLoading={resultBuscaInfoCidadaoState.isLoading}
      />
      <ModalStatusSincronizacaoCadsus
        loading={isModalSincronizacaoLoading}
        result={cadsusSyncResult}
        isOpen={isModalSincronizacaoOpen}
        isEdit={true}
        sincronizarCadsus={syncCadsus}
        setSyncModalState={setIsModalSincronizacaoOpen}
        hasCns={hasCnsState}
      />
    </PageContent>
  )
}
